import React, { Suspense } from "react";
import { Navigate, useRoutes } from "react-router-dom";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { paths } from "./paths";

import { Loader } from "@src/components/loader/Loader";
import { Home } from "@src/pages/home/Home";
import { LD_FLAGS } from "@src/config/launchDarkly";

const LazyAutologin = React.lazy(() => import("@src/pages/autologin/AutoLogin").then((x) => ({ default: x.AutoLogin })));
const LazyLogin = React.lazy(() => import("@src/pages/login/Login").then((x) => ({ default: x.Login })));
const LazySignup = React.lazy(() => import("@src/pages/signup/Signup").then((x) => ({ default: x.Signup })));
const LazyDashboard = React.lazy(() =>
	import("@src/pages/home/children/dashboard/Dashboard").then((x) => ({ default: x.Dashboard })),
);
const LazyPricing = React.lazy(() => import("@src/pages/home/children/pricing/Pricing").then((x) => ({ default: x.Pricing })));
const LazyEventlisting = React.lazy(() =>
	import("@src/pages/home/children/eventlist/EventList").then((x) => ({ default: x.EventList })),
);
const LazyReports = React.lazy(() => import("@src/pages/home/children/reports/Reports").then((x) => ({ default: x.Reports })));
const LazySfInsights = React.lazy(() =>
	import("@src/pages/home/children/reports/SfInsights").then((x) => ({ default: x.SfInsights })),
);
const LazyPriceVue = React.lazy(() =>
	import("@src/pages/home/children/pricevue/PriceVue").then((x) => ({ default: x.PriceVue })),
);
const LazyPurchasing = React.lazy(() =>
	import("@src/pages/home/children/purchasing/Purchasing").then((x) => ({ default: x.Purchasing })),
);
const LazyNotification = React.lazy(() =>
	import("@src/pages/home/children/notification/Notification").then((x) => ({ default: x.Notification })),
);
const LazySettings = React.lazy(() => import("@src/pages/settings/Settings").then((x) => ({ default: x.Settings })));
const LazySettingsAccount = React.lazy(() =>
	import("@src/pages/settings/SettingsAccount").then((x) => ({ default: x.SettingsAccount })),
);
const LazySettingsAutoPricer = React.lazy(() =>
	import("@src/pages/settings/SettingsAutoPricer").then((x) => ({ default: x.SettingsAutoPricer })),
);
const LazySettingsLegal = React.lazy(() =>
	import("@src/pages/settings/SettingsLegal").then((x) => ({ default: x.SettingsLegal })),
);
const LazySettingsNotifications = React.lazy(() =>
	import("@src/pages/settings/SettingsNotifications").then((x) => ({ default: x.SettingsNotifications })),
);
const LazySales = React.lazy(() => import("@src/pages/home/children/sales/Sales").then((x) => ({ default: x.Sales })));
const LazyAutoPo = React.lazy(() => import("@src/pages/home/children/autoPo/AutoPO").then((x) => ({ default: x.AutoPO })));
const LazyPasswordManager = React.lazy(() =>
	import("@src/pages/home/children/passwordManager/PasswordManager").then((x) => ({ default: x.PasswordManager })),
);
const LazyCreditCard = React.lazy(() =>
	import("@src/pages/home/children/creditCard/CreditCard").then((x) => ({ default: x.CreditCard })),
);
const LazyNewPo = React.lazy(() => import("@src/pages/home/children/newPo/NewPo").then((x) => ({ default: x.NewPo })));

export const Routes = () => {
	const ldClient = useLDClient();
	const enableSalesPage = ldClient?.variation(LD_FLAGS.enableSalesPage, false) ?? false;
	const enableManualPoCreation = ldClient?.variation(LD_FLAGS.enableManualPoCreation, false) ?? false;

	const salesRoute = enableSalesPage
		? [
				{
					element: (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazySales />
						</Suspense>
					),
					path: paths.sales,
				},
			]
		: [];

	return useRoutes([
		{
			path: paths.autologin,
			element: (
				<Suspense fallback={<Loader controlSize="xl" />}>
					<LazyAutologin />
				</Suspense>
			),
		},
		{
			path: paths.login,
			element: (
				<Suspense fallback={<Loader controlSize="xl" />}>
					<LazyLogin />
				</Suspense>
			),
		},
		{
			path: paths.signup,
			element: (
				<Suspense fallback={<Loader controlSize="xl" />}>
					<LazySignup />
				</Suspense>
			),
		},
		{
			path: paths.home,
			element: <Home />,
			children: [
				{
					index: true,
					element: (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazyPricing />
						</Suspense>
					),
				},
				{
					element: (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazyEventlisting />
						</Suspense>
					),
					path: paths.events,
				},
				{
					element: (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazyDashboard />
						</Suspense>
					),
					path: paths.dashboard,
				},
				{
					element: (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazyPricing />
						</Suspense>
					),
					path: paths.pricing,
				},
				{
					element: (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazyReports />
						</Suspense>
					),
					path: paths.reports,
				},
				{
					element: (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazySfInsights />
						</Suspense>
					),
					path: paths.datavue,
				},
				{
					element: (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazyPriceVue />
						</Suspense>
					),
					path: paths.pricevue,
				},
				...salesRoute,
				{
					element: (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazyPurchasing />
						</Suspense>
					),
					path: paths.purchasing,
				},
				{
					element: (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazyNotification />
						</Suspense>
					),
					path: paths.notification,
				},
				{
					path: paths.settings,
					element: (
						<Suspense fallback={<Loader controlSize="xl" isGrow />}>
							<LazySettings />
						</Suspense>
					),
					children: [
						{
							index: true,
							element: (
								<Suspense fallback={<Loader controlSize="xl" isGrow />}>
									<LazySettingsAccount />
								</Suspense>
							),
						},
						{
							path: paths.settingsAccount,
							element: (
								<Suspense fallback={<Loader controlSize="xl" isGrow />}>
									<LazySettingsAccount />
								</Suspense>
							),
						},
						{
							path: paths.settingsAutoPricer,
							element: (
								<Suspense fallback={<Loader controlSize="xl" isGrow />}>
									<LazySettingsAutoPricer />
								</Suspense>
							),
						},
						{
							path: paths.settingsLegal,
							element: (
								<Suspense fallback={<Loader controlSize="xl" isGrow />}>
									<LazySettingsLegal />
								</Suspense>
							),
						},
						{
							path: paths.settingsNotifications,
							element: (
								<Suspense fallback={<Loader controlSize="xl" isGrow />}>
									<LazySettingsNotifications />
								</Suspense>
							),
						},
					],
				},
				{
					element: (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazyAutoPo />
						</Suspense>
					),
					path: paths.autoPO,
				},
				{
					element: (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazyPasswordManager />
						</Suspense>
					),
					path: paths.passwordManager,
				},
				{
					element: (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazyCreditCard />
						</Suspense>
					),
					path: paths.creditCard,
				},
				{
					element: enableManualPoCreation ? (
						<Suspense fallback={<Loader controlSize="xl" />}>
							<LazyNewPo />
						</Suspense>
					) : (
						<Navigate to={paths.pricing} replace />
					),
					path: paths.newPO,
				},
			],
		},
		{
			path: "*",
			element: ldClient ? <Navigate to={paths.home} replace /> : <Loader controlSize="xl" />,
		},
	]);
};
